import { forwardRef } from 'react';

import { Alert, type AlertCssVariables, type AlertProps, type ElementProps } from '@mantine/core';
import { type ColorIntent, intentTint, resolveSemanticColor } from '@teo/design-tokens';

/**
 * The severities an alert can carry.
 *
 * A narrowing of {@link ColorIntent} rather than its own union, so a token intent
 * and an alert intent can never drift into two spellings of the same thing. The
 * narrowing widens silently when an intent is added; what forces a decision is the
 * exhaustiveness check under {@link ALERT_INTENTS}.
 *
 * `primary` is excluded because brand is not a severity and an alert wearing it
 * would be ornament; `neutral` because an alert that reports nothing is a
 * paragraph, and `info` already covers standing context.
 */
export type AlertIntent = Exclude<ColorIntent, 'primary' | 'neutral'>;

/**
 * Severity rank per intent. A `Record` IS exhaustive, so an intent added to the
 * tokens fails to compile here until it is ranked. That is the check `satisfies
 * readonly AlertIntent[]` on the list below cannot make: it tests that every entry
 * is an intent, not that every intent is an entry, so a missing one is silent.
 */
const ALERT_INTENT_RANK = {
  danger: 0,
  warning: 1,
  info: 2,
  success: 3,
} as const satisfies Record<AlertIntent, number>;

/** Every alert severity, most urgent first, for a legend or a demo. */
export const ALERT_INTENTS: readonly AlertIntent[] =
  // `Object.keys` is typed `string[]`, so some assertion is unavoidable; narrowing to
  // the Record's own keys keeps it tied to the map above rather than to a union that
  // could drift from it.
  (Object.keys(ALERT_INTENT_RANK) as (keyof typeof ALERT_INTENT_RANK)[]).sort(
    (a, b) => ALERT_INTENT_RANK[a] - ALERT_INTENT_RANK[b],
  );

/**
 * The canonical inline alert. Every banner reporting a failure, a warning, a
 * result or standing context is an `InlineAlert`; raw Mantine `<Alert>` is banned
 * outside the design system.
 *
 * One closed axis, `intent`, resolves all three colors from `@teo/design-tokens`,
 * so there is no per-hue special-casing here and no hardcoded hex. Mantine's
 * `<Alert>` supplies the layout and behavior (title, icon slot, close button, ref);
 * we override only its colors, through its documented CSS variables via the `vars`
 * prop.
 *
 * **The two locks, and exactly what each covers.** `AlertProps` extends `BoxProps`
 * and `StylesApiProps`, and `Box` applies `__vars`, `attributes` and the style props
 * (`bg`, `c`, `bd`) AFTER the resolved `vars`. Omitting `color` and `vars` alone
 * therefore leaves the fill reachable with no cast at all, and `attributes` erases
 * the token variables outright rather than merely overriding them. All of those are
 * omitted here, matching `StatusBadge`. The runtime lock is the narrower of the two
 * and covers only what a later prop can beat: `variant` and `vars` are re-applied
 * after the caller's spread.
 *
 * Neither lock is a guarantee against a determined caller, and the type lock is
 * weaker than it looks: TypeScript does not excess-property-check a JSX spread, so
 * `{...someWiderObject}` passes a closed prop with no cast and no lint. What the
 * `Omit` buys is that reaching the fill has to be deliberate and shows up in review
 * as a spread or a cast at the call site, rather than reading as an ordinary prop on
 * the element — though a wrapper that forwards a prop bag can still hide one.
 *
 * `opacity` is closed for the same reason even though it names no colour. It is a
 * Box style prop on the ROOT, so it opens a compositing group over everything the
 * component paints: at `opacity={0.6}` the message floor falls from 8.76:1 to
 * 3.07:1, under AA, and the border and title degrade with it. Unlike the hatches
 * below it has no non-colour use at a call site, so there is nothing to trade away
 * by closing it.
 *
 * `style`, `styles`, `className` and `classNames` stay open deliberately. They are
 * how a call site does layout, and closing them would push callers into wrapper
 * divs. They can carry color too, which is the residual this component does not
 * defend against and review has to.
 *
 * `icon` stays a plain pass-through rather than defaulting to the intent's glyph.
 * The governed marks in `intentIndicator` exist because a 4px edge is the only
 * other signal a card surface has; an alert states its severity in its message,
 * which is why `children` is required here.
 */
export interface InlineAlertProps
  extends
    Omit<
      AlertProps,
      | 'variant'
      | 'color'
      | 'autoContrast'
      | 'vars'
      | '__vars'
      | 'attributes'
      | 'bg'
      | 'c'
      | 'bd'
      | 'opacity'
      | 'unstyled'
      // Closed HERE, not on the clause below. `AlertProps` already extends
      // `ElementProps<'div', 'title'>`, so `ElementProps<'div', keyof AlertProps>`
      // excludes every div prop and is the empty set — omitting from it does
      // nothing. `StatusBadge` closes the same channel on its second clause and is
      // right to, because `BadgeProps` does NOT extend `ElementProps`. The idiom is
      // not portable between the two; the interface it applies to decides.
      | 'dangerouslySetInnerHTML'
      | 'title'
    >,
    ElementProps<'div', keyof AlertProps> {
  /** Semantic severity. Drives the color, never the caller's raw hue. */
  intent: AlertIntent;
  /**
   * The claim, as a string. Closed for the same reason as `children`: a node title
   * takes `<Text c="…">` just as readily, which reapplies the theme colour and
   * opacity to the one line the intent ink is most load-bearing on. Narrowing
   * `children` alone would have left the larger of the two texts open.
   */
  title?: string;
  /**
   * What the alert says, as a string.
   *
   * Closed to `string` for the reason `PageTitle` closes its own. A node body is
   * what let every call site reach for `<Text size="xs">`, and the theme gives
   * `Text` a `c` and an `opacity` — the two channels this component closes on
   * itself. A component that locks its colour and then documents a usage handing
   * colour back to a child has locked nothing.
   *
   * The README's "Copy that sits on a filled surface" is the neighbouring rule, not
   * this one: it governs dark fills, where the override makes text vanish, and it
   * calls the light-surface case merely "dimmer than intended". The tint here is
   * light, so nothing disappeared. What made it worth fixing is narrower — `intent`
   * claims to resolve the ink, and it did not reach the body at all.
   */
  children: string;
}

/**
 * Pinned rather than exposed. `light` is the tint treatment the `intentTint` group
 * is built for; every other Mantine variant (filled, outline, white, transparent)
 * takes its fill from a Mantine palette entry this component never names, so
 * offering the axis would reopen the color channel the `Omit` above closes.
 */
const MANTINE_VARIANT = 'light';

/**
 * The Mantine Alert CSS variables for an intent, all three drawn from `intentTint`.
 *
 * The border is deliberately `intentTint.border` and not the light-button border:
 * the token layer notes that the button shade sits below 1.4.11's 3:1 because a
 * light button's tint fill is its primary edge, and reusing it here would leave an
 * alert on a tinted card with no boundary of its own.
 */
function computeAlertColorVars(
  intent: AlertIntent,
): Partial<Record<AlertCssVariables['root'], string>> {
  const { bg, fg, border } = intentTint[intent];
  return {
    '--alert-bg': resolveSemanticColor(bg),
    '--alert-color': resolveSemanticColor(fg),
    '--alert-bd': `1px solid ${resolveSemanticColor(border)}`,
  };
}

export const InlineAlert = forwardRef<HTMLDivElement, InlineAlertProps>(function InlineAlert(
  { intent, children, ...props },
  ref,
) {
  return (
    // props spreads first so the token-derived variant/vars always win — the typed
    // Omit closes the escape hatch at compile time, this closes it at runtime.
    <Alert
      {...props}
      ref={ref}
      variant={MANTINE_VARIANT}
      vars={() => ({ root: computeAlertColorVars(intent) })}
    >
      {/* A plain span, per the README's on-surface copy rule: Mantine `Text` would
          reapply the theme's `c` and `opacity` here. `--alert-color` cannot do this
          job either — Mantine scopes `.message` to `--mantine-color-black` on every
          variant except `filled`, and this one pins `light`. */}
      <span style={messageStyle(intent)}>{children}</span>
    </Alert>
  );
});

/**
 * The body's own ink and size. Not a Mantine variable: it lands on a span this
 * component renders, which is the only thing that beats the `.message` rule.
 */
function messageStyle(intent: AlertIntent): {
  color: string;
  fontSize: string;
  lineHeight: string;
} {
  return {
    color: resolveSemanticColor(intentTint[intent].fg),
    fontSize: 'var(--mantine-font-size-xs)',
    // The value `Text size="xs"` supplied, kept as a floor rather than for looks.
    // Measured in Chromium: today it changes no geometry, because `.message` sets
    // its own strut (14px x the ambient 1.55 = 21.7px) which the span's 16.8px
    // never exceeds, and multi-line height is identical either way. It is not
    // insulation against a LOOSER ambient — a container with a large `line-height`
    // still grows the body, because that leaks through `.message`, and closing that
    // would mean pinning `.message` itself. It does floor the body against a
    // TIGHTER one: below roughly 1.2 the strut stops dominating and the span's own
    // value governs, so at ambient 1.0 the body holds 16.8px where un-pinned it
    // collapses to 14px.
    lineHeight: 'var(--mantine-line-height-xs)',
  };
}

export const __private = { computeAlertColorVars, messageStyle, MANTINE_VARIANT };
